import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import ProgressBar from '@ramonak/react-progress-bar';
import {FormattedMessage, defineMessages, injectIntl, intlShape} from 'react-intl';

import Modal from '../../containers/modal.jsx';
import StepIndicator from './step-indicator.jsx';

import styles from './firmware-wizard-modal.css';

/**
 * FirmwareWizardModalComponent — presentational 3-step Firmware OTA wizard
 * (GEN-05 / Phase 2 / Plan 02-05). Renders inside Scratch's <Modal> wrapper
 * (react-modal under the hood). All user-facing strings flow through
 * react-intl. Container drives step transitions + mock progress timer.
 *
 * Implemented as a class (rather than arrow function) so onChange handlers
 * can be bound via lodash.bindall — avoids react/jsx-no-bind from inline
 * arrow allocations in the dropdowns.
 *
 * Visual reference: DG-15..DG-17 + cards.jsx step-pip pattern, themed in
 * NOC navy. ProgressBar borrowed from update-modal.jsx (already installed).
 */

const messages = defineMessages({
    modalTitle: {
        defaultMessage: 'Cập nhật Firmware',
        description: 'Firmware wizard modal title',
        id: 'gen.firmware.modal.title'
    },
    step1Label: {
        defaultMessage: 'Chọn robot',
        description: 'Step 1 label — choose robot',
        id: 'gen.firmware.step.1'
    },
    step2Label: {
        defaultMessage: 'Chọn firmware',
        description: 'Step 2 label — choose firmware version',
        id: 'gen.firmware.step.2'
    },
    step3Label: {
        defaultMessage: 'Cập nhật',
        description: 'Step 3 label — upload progress',
        id: 'gen.firmware.step.3'
    },
    step1Prompt: {
        defaultMessage: 'Chọn robot cần cập nhật firmware:',
        description: 'Step 1 prompt above robot dropdown',
        id: 'gen.firmware.step.1.prompt'
    },
    step2Prompt: {
        defaultMessage: 'Chọn phiên bản firmware:',
        description: 'Step 2 prompt above version dropdown',
        id: 'gen.firmware.step.2.prompt'
    },
    step3Prompt: {
        defaultMessage: 'Đang cập nhật firmware vào robot...',
        description: 'Step 3 prompt while uploading',
        id: 'gen.firmware.step.3.prompt'
    },
    releaseDate: {
        defaultMessage: 'Ngày phát hành',
        description: 'Version metadata label — release date',
        id: 'gen.firmware.meta.releaseDate'
    },
    size: {
        defaultMessage: 'Dung lượng',
        description: 'Version metadata label — size',
        id: 'gen.firmware.meta.size'
    },
    notes: {
        defaultMessage: 'Ghi chú',
        description: 'Version metadata label — release notes',
        id: 'gen.firmware.meta.notes'
    },
    next: {
        defaultMessage: 'Tiếp theo',
        description: 'Wizard next button',
        id: 'gen.firmware.btn.next'
    },
    back: {
        defaultMessage: 'Quay lại',
        description: 'Wizard back button',
        id: 'gen.firmware.btn.back'
    },
    cancel: {
        defaultMessage: 'Huỷ',
        description: 'Wizard cancel button',
        id: 'gen.firmware.btn.cancel'
    },
    close: {
        defaultMessage: 'Đóng',
        description: 'Close button on success state',
        id: 'gen.firmware.btn.close'
    },
    success: {
        defaultMessage: 'Cập nhật firmware thành công!',
        description: 'Success message after 100% progress',
        id: 'gen.firmware.success'
    },
    selectRobotPlaceholder: {
        defaultMessage: '— Chọn robot —',
        description: 'Robot dropdown placeholder',
        id: 'gen.firmware.select.robot'
    },
    selectVersionPlaceholder: {
        defaultMessage: '— Chọn phiên bản —',
        description: 'Version dropdown placeholder',
        id: 'gen.firmware.select.version'
    },
    transportPrompt: {
        defaultMessage: 'Chọn phương thức cập nhật:',
        description: 'Transport selector prompt (UC-05 S-3/S-4)',
        id: 'gen.firmware.transport.prompt'
    },
    transportUsb: {
        defaultMessage: 'Cáp USB',
        description: 'Firmware transport — USB cable (UC-05 S-4)',
        id: 'gen.firmware.transport.usb'
    },
    transportUsbDesc: {
        defaultMessage: 'Nạp firmware qua dây cáp USB',
        description: 'Firmware transport description — USB',
        id: 'gen.firmware.transport.usb.desc'
    },
    transportWifi: {
        defaultMessage: 'Wi-Fi (OTA)',
        description: 'Firmware transport — Wi-Fi OTA (UC-05 S-3)',
        id: 'gen.firmware.transport.wifi'
    },
    transportWifiDesc: {
        defaultMessage: 'Cập nhật từ xa qua mạng Wi-Fi',
        description: 'Firmware transport description — Wi-Fi OTA',
        id: 'gen.firmware.transport.wifi.desc'
    }
});

class FirmwareWizardModalComponent extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleRobotChange',
            'handleVersionChange',
            'handleSelectUsb',
            'handleSelectWifi'
        ]);
    }
    handleRobotChange (e) {
        this.props.onSelectRobot(e.target.value || null);
    }
    handleVersionChange (e) {
        this.props.onSelectVersion(e.target.value || null);
    }
    handleSelectUsb () {
        this.props.onSelectTransport('usb');
    }
    handleSelectWifi () {
        this.props.onSelectTransport('wifi');
    }
    render () {
        const {
            firmwares,
            intl,
            isComplete,
            onRequestClose,
            onNext,
            onBack,
            progress,
            robots,
            selectedRobotId,
            selectedVersion,
            transport,
            step
        } = this.props;
        const stepLabels = [
            intl.formatMessage(messages.step1Label),
            intl.formatMessage(messages.step2Label),
            intl.formatMessage(messages.step3Label)
        ];
        const versionsForRobot = (selectedRobotId && firmwares[selectedRobotId]) || [];
        const selectedVersionMeta = versionsForRobot.find(v => v.version === selectedVersion);

        // Step 1 now also requires a transport choice (UC-05 S-3 Wi-Fi/OTA vs
        // S-4 USB cable) before the mock progress can begin.
        const canAdvance =
            (step === 0 && Boolean(selectedRobotId)) ||
            (step === 1 && Boolean(selectedVersion) && Boolean(transport));

        return (
            <Modal
                className={styles.modalContent}
                contentLabel={intl.formatMessage(messages.modalTitle)}
                onRequestClose={onRequestClose}
            >
                <div className={styles.body}>
                    <StepIndicator
                        currentStep={step}
                        labels={stepLabels}
                        totalSteps={3}
                    />
                    <div className={styles.stepContent}>
                        {step === 0 ? (
                            <div>
                                <p className={styles.prompt}>
                                    <FormattedMessage {...messages.step1Prompt} />
                                </p>
                                <select
                                    className={styles.select}
                                    value={selectedRobotId || ''}
                                    onChange={this.handleRobotChange}
                                >
                                    <option value="">
                                        {intl.formatMessage(messages.selectRobotPlaceholder)}
                                    </option>
                                    {robots.map(r => (
                                        <option
                                            key={r.id}
                                            value={r.id}
                                        >
                                            {`${r.name} (${r.type}) — fw ${r.firmwareVersion}`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        ) : null}
                        {step === 1 ? (
                            <div>
                                <p className={styles.prompt}>
                                    <FormattedMessage {...messages.step2Prompt} />
                                </p>
                                <select
                                    className={styles.select}
                                    value={selectedVersion || ''}
                                    onChange={this.handleVersionChange}
                                >
                                    <option value="">
                                        {intl.formatMessage(messages.selectVersionPlaceholder)}
                                    </option>
                                    {versionsForRobot.map(v => (
                                        <option
                                            key={v.version}
                                            value={v.version}
                                        >
                                            {`v${v.version} — ${v.releaseDate} — ${v.sizeKB} KB`}
                                        </option>
                                    ))}
                                </select>
                                {selectedVersionMeta ? (
                                    <div className={styles.versionMeta}>
                                        <div>
                                            <strong>
                                                <FormattedMessage {...messages.releaseDate} />
                                            </strong>
                                            {`: ${selectedVersionMeta.releaseDate}`}
                                        </div>
                                        <div>
                                            <strong>
                                                <FormattedMessage {...messages.size} />
                                            </strong>
                                            {`: ${selectedVersionMeta.sizeKB} KB`}
                                        </div>
                                        <div>
                                            <strong>
                                                <FormattedMessage {...messages.notes} />
                                            </strong>
                                            {`: ${selectedVersionMeta.releaseNotes}`}
                                        </div>
                                    </div>
                                ) : null}
                                {selectedVersion ? (
                                    <div className={styles.transportSection}>
                                        <p className={styles.prompt}>
                                            <FormattedMessage {...messages.transportPrompt} />
                                        </p>
                                        <div className={styles.transportToggle}>
                                            <button
                                                className={transport === 'usb' ?
                                                    styles.transportOptionActive :
                                                    styles.transportOption}
                                                type="button"
                                                data-transport="usb"
                                                onClick={this.handleSelectUsb}
                                            >
                                                <span
                                                    className={styles.transportIcon}
                                                    aria-hidden="true"
                                                >
                                                    {'🔌'}
                                                </span>
                                                <span className={styles.transportLabel}>
                                                    {intl.formatMessage(messages.transportUsb)}
                                                </span>
                                                <span className={styles.transportDesc}>
                                                    {intl.formatMessage(messages.transportUsbDesc)}
                                                </span>
                                            </button>
                                            <button
                                                className={transport === 'wifi' ?
                                                    styles.transportOptionActive :
                                                    styles.transportOption}
                                                type="button"
                                                data-transport="wifi"
                                                onClick={this.handleSelectWifi}
                                            >
                                                <span
                                                    className={styles.transportIcon}
                                                    aria-hidden="true"
                                                >
                                                    {'📡'}
                                                </span>
                                                <span className={styles.transportLabel}>
                                                    {intl.formatMessage(messages.transportWifi)}
                                                </span>
                                                <span className={styles.transportDesc}>
                                                    {intl.formatMessage(messages.transportWifiDesc)}
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                        {step === 2 ? (
                            <div className={styles.progressWrap}>
                                {isComplete ? (
                                    <div className={styles.successBox}>
                                        <span
                                            className={styles.successCheck}
                                            aria-hidden="true"
                                        >
                                            {'✓'}
                                        </span>
                                        <FormattedMessage {...messages.success} />
                                    </div>
                                ) : (
                                    <div>
                                        <p className={styles.prompt}>
                                            <FormattedMessage {...messages.step3Prompt} />
                                        </p>
                                        <div className={styles.progressMeta}>
                                            <span>
                                                {selectedVersionMeta ?
                                                    `firmware-${selectedVersionMeta.version}.bin` :
                                                    'firmware.bin'}
                                            </span>
                                            <span className={styles.progressPercent}>
                                                {`${Math.round(progress)}%`}
                                            </span>
                                        </div>
                                        <ProgressBar
                                            baseBgColor="#D9E3F2"
                                            bgColor="#162A75"
                                            completed={Math.round(progress)}
                                            height="8px"
                                            isLabelVisible={false}
                                        />
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </div>
                    <div className={styles.footer}>
                        {step < 2 ? (
                            <button
                                className={styles.btnSecondary}
                                type="button"
                                onClick={onRequestClose}
                            >
                                <FormattedMessage {...messages.cancel} />
                            </button>
                        ) : null}
                        {step > 0 && step < 2 ? (
                            <button
                                className={styles.btnSecondary}
                                type="button"
                                onClick={onBack}
                            >
                                <FormattedMessage {...messages.back} />
                            </button>
                        ) : null}
                        {step < 2 ? (
                            <button
                                className={styles.btnPrimary}
                                disabled={!canAdvance}
                                type="button"
                                onClick={onNext}
                            >
                                <FormattedMessage {...messages.next} />
                            </button>
                        ) : null}
                        {step === 2 && isComplete ? (
                            <button
                                className={styles.btnPrimary}
                                type="button"
                                onClick={onRequestClose}
                            >
                                <FormattedMessage {...messages.close} />
                            </button>
                        ) : null}
                    </div>
                </div>
            </Modal>
        );
    }
}

FirmwareWizardModalComponent.propTypes = {
    firmwares: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.shape({
        version: PropTypes.string,
        releaseDate: PropTypes.string,
        sizeKB: PropTypes.number,
        releaseNotes: PropTypes.string
    }))).isRequired,
    intl: intlShape.isRequired,
    isComplete: PropTypes.bool,
    onBack: PropTypes.func.isRequired,
    onNext: PropTypes.func.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    onSelectRobot: PropTypes.func.isRequired,
    onSelectTransport: PropTypes.func.isRequired,
    onSelectVersion: PropTypes.func.isRequired,
    progress: PropTypes.number.isRequired,
    robots: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        type: PropTypes.string,
        firmwareVersion: PropTypes.string
    })).isRequired,
    selectedRobotId: PropTypes.string,
    selectedVersion: PropTypes.string,
    step: PropTypes.number.isRequired,
    transport: PropTypes.oneOf(['usb', 'wifi'])
};

export default injectIntl(FirmwareWizardModalComponent);
